import React, { Suspense } from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { initSentry } from './sentry'
import ErrorBoundary from './ErrorBoundary'

// Initialize Sentry before rendering (no-op if VITE_SENTRY_DSN is not set)
initSentry()
import App from './App'
import LoginPage from './LoginPage'
import { isLoggedIn, USER_KEY } from './api'
import CookieConsent from './components/CookieConsent'
import { ToastProvider } from './components/Toast'
import './index.css'
import { Navigate } from 'react-router-dom'

// Lazy-loaded route components for code-splitting
const LandingPage = React.lazy(() => import('./LandingPage'))
const SignupPage = React.lazy(() => import('./SignupPage'))
const ForgotPasswordPage = React.lazy(() => import('./ForgotPasswordPage'))
const ResetPasswordPage = React.lazy(() => import('./ResetPasswordPage'))
const VerifyEmailPage = React.lazy(() => import('./VerifyEmailPage'))
const AdminDashboard = React.lazy(() => import('./AdminDashboard'))
const PrivacyPolicyPage = React.lazy(() => import('./PrivacyPolicyPage'))
const TermsOfServicePage = React.lazy(() => import('./TermsOfServicePage'))
const SecurityPage = React.lazy(() => import('./SecurityPage'))
const EHRIntegrationPage = React.lazy(() => import('./EHRIntegrationPage'))
const MarketIntelDashboard = React.lazy(() => import('./MarketIntelDashboard'))
const NotFoundPage = React.lazy(() => import('./NotFoundPage'))
const LMNDrugPage = React.lazy(() => import('./LMNDrugPage'))
const LMNIndexPage = React.lazy(() => import('./LMNDrugPage').then(m => ({ default: m.LMNIndexPage })))
const PayerPage = React.lazy(() => import('./PayerPage'))
const PayerIndexPage = React.lazy(() => import('./PayerPage').then(m => ({ default: m.PayerIndexPage })))
const BlogIndexPage = React.lazy(() => import('./BlogPage').then(m => ({ default: m.BlogIndexPage })))
const BlogPostPage = React.lazy(() => import('./BlogPage'))
const LMNGraderPage = React.lazy(() => import('./LMNGraderPage'))
const ComparisonPage = React.lazy(() => import('./ComparisonPage'))
const ComparisonIndexPage = React.lazy(() => import('./ComparisonPage').then(m => ({ default: m.ComparisonIndexPage })))
const StatePage = React.lazy(() => import('./StatePage'))
const StateIndexPage = React.lazy(() => import('./StatePage').then(m => ({ default: m.StateIndexPage })))
const BCBSPage = React.lazy(() => import('./BCBSPage'))
const BCBSIndexPage = React.lazy(() => import('./BCBSPage').then(m => ({ default: m.BCBSIndexPage })))

function LoadingFallback() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-gray-500 text-lg">Loading...</div>
    </div>
  )
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  if (!isLoggedIn()) return <Navigate to="/login" replace />
  return <>{children}</>
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  if (!isLoggedIn()) return <Navigate to="/login" replace />
  try {
    const userData = JSON.parse(localStorage.getItem(USER_KEY) || '{}')
    if (!userData.is_admin && !userData.isAdmin) return <Navigate to="/app" replace />
  } catch {
    return <Navigate to="/app" replace />
  }
  return <>{children}</>
}

const container = document.getElementById('root')!

// Prevent duplicate renders from Vite HMR re-executing this module
const root = (window as any).__REACT_ROOT__ ?? ReactDOM.createRoot(container);
(window as any).__REACT_ROOT__ = root

root.render(
  <ErrorBoundary>
    <BrowserRouter>
      <ToastProvider>
      <Suspense fallback={<LoadingFallback />}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/app" element={
          <ProtectedRoute>
            <App />
          </ProtectedRoute>
        } />
        {/* Demo route removed — use demo@rxcheckup.ai account for sales demos */}
        <Route path="/admin" element={
          <AdminRoute>
            <AdminDashboard />
          </AdminRoute>
        } />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />
        <Route path="/security" element={<SecurityPage />} />
        <Route path="/ehr-integrations" element={<EHRIntegrationPage />} />
        <Route path="/market-intel" element={
          <AdminRoute>
            <MarketIntelDashboard />
          </AdminRoute>
        } />
        <Route path="/lmn" element={<LMNIndexPage />} />
        <Route path="/lmn/:slug" element={<LMNDrugPage />} />
        <Route path="/payer" element={<PayerIndexPage />} />
        <Route path="/payer/:slug" element={<PayerPage />} />
        <Route path="/blog" element={<BlogIndexPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/tools/lmn-grader" element={<LMNGraderPage />} />
        <Route path="/vs" element={<ComparisonIndexPage />} />
        <Route path="/vs/:competitor" element={<ComparisonPage />} />
        <Route path="/state" element={<StateIndexPage />} />
        <Route path="/state/:slug" element={<StatePage />} />
        <Route path="/bcbs" element={<BCBSIndexPage />} />
        <Route path="/bcbs/:slug" element={<BCBSPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </Suspense>
      <CookieConsent />
      </ToastProvider>
    </BrowserRouter>
  </ErrorBoundary>,
)
